import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router';
import {
  ArrowLeft,
  Bot,
  AlertCircle,
  Radio,
  FlaskConical,
  Shield,
  Target,
  Settings,
  Wallet,
  Copy,
  Lock,
  AlertTriangle,
  Info,
  ChevronRight,
  RefreshCw,
} from 'lucide-react';
import { useCreateBot, useBots } from '@/hooks/use-bots';
import { useMt5Accounts, useSyncMt5Balance } from '@/hooks/use-accounts';
import { useStrategies } from '@/hooks/use-strategies';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Modal } from '@/components/ui/modal';
import { usePlanLimits } from '@/hooks/use-plan-limits';
import { useSubscribeToCopy } from '@/hooks/use-copy-trade';

type BotMode = 'LIVE' | 'PAPER';
type AllocMode = 'FIXED_LOT' | 'FIXED_MULT';

const ALLOC_MODE_INFO: Record<AllocMode, { label: string; description: string }> = {
  FIXED_MULT: { label: 'Fixed Multiple', description: 'Scale strategy volume by a multiplier' },
  FIXED_LOT: { label: 'Fixed Lots', description: 'Every trade uses the same lot size' },
};

/** Check if a strategy symbol is supported by the selected account's symbol_specs */
function isSymbolSupported(
  strategySymbol: string | null | undefined,
  brokerSymbols: string[] | null,
  brokerSymbolsUpper: string[] | null,
): boolean {
  if (!strategySymbol || !brokerSymbols || !brokerSymbolsUpper) return true; // no data to check
  const canonical = strategySymbol.toUpperCase().replace(/[+#!]+$/, '').replace(/\.[a-zA-Z]+$/, '').replace(/_[a-z]+$/i, '');
  return brokerSymbolsUpper.includes(canonical) || brokerSymbols.some((b) => b.toUpperCase() === strategySymbol.toUpperCase());
}

export default function BotNewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const createBot = useCreateBot();
  const subscribeToCopy = useSubscribeToCopy();
  const syncBalance = useSyncMt5Balance();

  // Copy Trading mode — passed via query params from signal page
  const signalId = searchParams.get('signalId');
  const signalName = searchParams.get('signalName');
  const signalSymbol = searchParams.get('symbol');
  const signalDescription = searchParams.get('description');
  const isCopyTrade = !!signalId;

  // Dicebear avatar for copy trading signal
  const signalAvatar = signalName
    ? `https://api.dicebear.com/9.x/shapes/svg?seed=${encodeURIComponent(signalName)}&backgroundColor=f97316,fb923c,fbbf24&shape1Color=f97316,ea580c`
    : null;
  const { data: accounts, isLoading: accountsLoading } = useMt5Accounts();
  const { data: strategies, isLoading: strategiesLoading } = useStrategies({ exclude: 'ARCHIVED' });
  const { data: existingBots } = useBots();

  const limits = usePlanLimits();
  const botCount = (existingBots ?? []).filter(b => !b.archived_at).length;
  const maxBots = limits.maxBotsPerUser;
  const atBotLimit = botCount >= maxBots;

  // --- Form state ---
  const [mode, setMode] = useState<BotMode>('LIVE');
  const [name, setName] = useState(isCopyTrade ? `Copy: ${signalName}` : '');
  const [mt5AccountId, setMt5AccountId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [symbol, setSymbol] = useState(signalSymbol || 'XAUUSD');
  const [capital, setCapital] = useState('50');
  const [slPct, setSlPct] = useState('10');
  const [tpEnabled, setTpEnabled] = useState(false);
  const [tpPct, setTpPct] = useState('50');
  const [allocModeEnabled, setAllocModeEnabled] = useState(isCopyTrade ? true : false);
  const [allocMode, setAllocMode] = useState<AllocMode>(isCopyTrade ? 'FIXED_MULT' : 'FIXED_LOT');
  const [allocFixedLot, setAllocFixedLot] = useState('');
  const [allocMultiplier, setAllocMultiplier] = useState('1');
  const [error, setError] = useState('');
  const [errorField, setErrorField] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showAccountPicker, setShowAccountPicker] = useState(false);
  const [showStrategyPicker, setShowStrategyPicker] = useState(false);

  const isLoading = accountsLoading || strategiesLoading;
  const allPublished = (strategies ?? []).filter(
    (s) => s.status === 'PUBLISHED' || s.latest_version > 0,
  );

  // Resolve selected account and its symbols
  const selectedAccount = accounts?.find((a) => a.id === mt5AccountId);
  const brokerSymbols = selectedAccount?.symbol_specs ? Object.keys(selectedAccount.symbol_specs) : null;
  const brokerSymbolsUpper = brokerSymbols?.map((s) => s.toUpperCase().replace(/[+#!]+$/, '').replace(/\.[a-zA-Z]+$/, '').replace(/_[a-z]+$/i, '')) ?? null;

  // Auto-select first account if only one (required for both LIVE and PAPER)
  const firstAccount = accounts?.[0];
  if (!mt5AccountId && firstAccount) {
    setMt5AccountId(firstAccount.id);
  }

  const isLive = mode === 'LIVE';
  const hasAccounts = accounts && accounts.length > 0;
  const selectedStrategy = allPublished.find((s) => s.id === templateId);

  // Compute dollar amounts for SL/TP descriptions
  const capitalNum = parseFloat(capital) || 0;
  const exceedsCapital = isLive && !!selectedAccount && capitalNum > 0 && capitalNum > selectedAccount.unallocated_capital;
  const slDollar = capitalNum > 0 ? (parseFloat(slPct) / 100) * capitalNum : 0;
  const tpDollar = capitalNum > 0 ? (parseFloat(tpPct) / 100) * capitalNum : 0;

  // --- Validate & show preview ---
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setErrorField(null);

    if (!mt5AccountId) {
      setErrorField('account');
      return;
    }
    if (!isCopyTrade) {
      if (!templateId) {
        setErrorField('strategy');
        return;
      }
      // Warn if strategy symbol is unsupported
      if (selectedStrategy?.symbol && !isSymbolSupported(selectedStrategy.symbol, brokerSymbols, brokerSymbolsUpper)) {
        setError(`Symbol "${selectedStrategy.symbol}" is not supported by the selected MT5 account.`);
        setErrorField('strategy');
        return;
      }
    }
    if (exceedsCapital) {
      setErrorField('capital');
      return;
    }
    const slNum = parseFloat(slPct);
    if (isNaN(slNum) || slNum < 5 || slNum > 95) {
      setError('Stop Loss must be between 5% and 95%.');
      return;
    }

    if (tpEnabled) {
      const tpNum = parseFloat(tpPct);
      if (isNaN(tpNum) || tpNum < 5 || tpNum > 95) {
        setError('Take Profit must be between 5% and 95%.');
        return;
      }
    }

    if (allocModeEnabled) {
      if (allocMode === 'FIXED_LOT') {
        const lot = parseFloat(allocFixedLot);
        const spec = selectedAccount?.symbol_specs?.[symbol];
        if (spec && !isNaN(lot) && lot < spec.minVolume) {
          setError(`Fixed lot (${lot}) is below broker minimum (${spec.minVolume}) for ${symbol}.`);
          return;
        }
        if (spec && !isNaN(lot) && lot > spec.maxVolume) {
          setError(`Fixed lot (${lot}) exceeds broker maximum (${spec.maxVolume}) for ${symbol}.`);
          return;
        }
      }
    }

    setShowPreview(true);
  };

  // --- Confirm creation from preview modal ---
  const handleConfirmCreate = async () => {
    setError('');
    const parsedCapital = parseFloat(capital);
    const slNum = parseFloat(slPct);
    const tpNum = tpEnabled ? parseFloat(tpPct) : undefined;
    try {
      if (isCopyTrade && signalId) {
        // Copy trading: use subscribe endpoint (creates bot + subscription)
        const sub = await subscribeToCopy.mutateAsync({
          signalId,
          mt5AccountId,
          allocationMode: allocModeEnabled ? allocMode : 'FIXED_MULT',
          ...(allocModeEnabled && allocMode === 'FIXED_LOT' && { fixedLot: parseFloat(allocFixedLot) }),
          ...(allocModeEnabled && allocMode === 'FIXED_MULT' && { multiplier: parseFloat(allocMultiplier) }),
          ...(!allocModeEnabled && { multiplier: 1 }),
          assignedCapital: parsedCapital,
        });
        navigate(`/bots/${sub.copierBot?.id || sub.id}`);
      } else {
        // Regular bot creation
        const result = await createBot.mutateAsync({
          name: name.trim(),
          mode,
          mt5AccountId: mt5AccountId || undefined,
          templateId: templateId || undefined,
          symbol: symbol || undefined,
          assigned_capital: parsedCapital,
          sl_pct: slNum,
          tp_pct: tpNum,
          ...(allocModeEnabled && {
            allocation_mode: allocMode,
            ...(allocMode === 'FIXED_LOT' && { alloc_fixed_lot: parseFloat(allocFixedLot) }),
            ...(allocMode === 'FIXED_MULT' && { alloc_multiplier: parseFloat(allocMultiplier) }),
          }),
        });
        navigate(`/bots/${result.id}`);
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err as Error)?.message ??
        'Failed to create bot.';
      setShowPreview(false);
      setError(typeof msg === 'string' ? msg : 'Failed to create bot.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-2xl mx-auto">
      {/* Back Link */}
      <Link
        to="/bots"
        className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-ink-3 hover:text-orange transition-colors"
      >
        <ArrowLeft size={12} />
        All Bots
      </Link>

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-surface-2 flex items-center justify-center">
          <Bot size={18} className="text-orange" />
        </div>
        <div>
          <h1 className="text-lg font-extrabold tracking-tight">Create Bot</h1>
          <p className="text-xs text-ink-3">Configure a new trading bot</p>
        </div>
      </div>

      {/* Plan limit banner */}
      {atBotLimit && (
        <div className="flex items-start gap-2.5 bg-amber/5 border border-amber/15 rounded-lg px-4 py-3">
          <Lock size={14} className="text-amber flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-[11px] font-bold text-amber">Bot limit reached</p>
            <p className="text-[10px] text-amber/80 mt-0.5">
              Your beta plan allows {maxBots} bot{maxBots !== 1 ? 's' : ''}.
              You currently have {botCount}.
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* ─── Copy Trading Banner ─── */}
        {isCopyTrade && (
          <div className="glass-sm rounded-lg p-4 border border-orange/20">
            <div className="flex items-center gap-3">
              {signalAvatar && (
                <img src={signalAvatar} alt={signalName || ''} className="w-10 h-10 rounded-full bg-surface flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-ink">Copy Trading: {signalName}</p>
                {signalDescription ? (
                  <p className="text-[10px] text-ink-4 mt-0.5 line-clamp-2">{signalDescription}</p>
                ) : (
                  <p className="text-[10px] text-ink-4 mt-0.5">Mirrors trades from this signal to your MT5 account</p>
                )}
                {signalSymbol && (
                  <span className="inline-block mt-1 text-[9px] font-mono font-semibold text-orange bg-orange/10 rounded px-1.5 py-0.5">
                    {signalSymbol}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ─── Section 1: Mode (hidden for copy trading — live only) ─── */}
        {!isCopyTrade && (
        <div className="glass-sm rounded-lg p-4">
          <p className="text-[11px] font-bold text-ink-3 uppercase tracking-wider mb-3">Mode</p>
          <div className="grid grid-cols-2 gap-2">
            <ModeButton
              active={mode === 'LIVE'}
              onClick={() => setMode('LIVE')}
              icon={<Radio size={16} />}
              label="Live"
              description="Real MT5 account, live execution"
              accentClass="green"
            />
            <ModeButton
              active={mode === 'PAPER'}
              onClick={() => setMode('PAPER')}
              icon={<FlaskConical size={16} />}
              label="Paper"
              description="Simulated execution, no real money"
              accentClass="blue"
            />
          </div>
        </div>
        )}

        {/* ─── Section 2: Bot Details ─── */}
        <div className="glass-sm rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-1.5 mb-3">
            <Settings size={12} className="text-orange" />
            <p className="text-[11px] font-bold text-ink-3 uppercase tracking-wider">
              Bot Details
            </p>
          </div>

          {/* Bot Name */}
          <Input
            label="Bot Name *"
            placeholder="e.g., Gold Scalper"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />

          {/* MT5 Account — selector button */}
          <div>
            <label className="block text-[11px] font-semibold text-ink-3 mb-1.5">
              MT5 Account *
            </label>
            {hasAccounts ? (
              <button
                type="button"
                onClick={() => { setShowAccountPicker(true); setErrorField(null); }}
                className={`w-full text-left rounded-lg border px-3 py-2 transition-all cursor-pointer flex items-center justify-between bg-surface ${
                  errorField === 'account'
                    ? 'border-orange'
                    : 'border-glass-border hover:border-glass-border/80'
                }`}
              >
                {selectedAccount ? (
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Wallet size={14} className="text-orange flex-shrink-0" />
                    <span className="text-sm text-ink truncate">{selectedAccount.name || selectedAccount.login_id}</span>
                  </div>
                ) : (
                  <span className="text-sm text-ink-4">Select an account...</span>
                )}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {selectedAccount?.synced_balance != null && (
                    <span className="text-[9px] font-mono font-semibold text-ink-2 bg-surface-2 rounded px-1.5 py-0.5">
                      ${selectedAccount.synced_balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  )}
                  {selectedAccount && (
                    <span className="text-[9px] font-mono font-semibold text-ink-2 bg-surface-2 rounded px-1.5 py-0.5">
                      1:{selectedAccount.leverage}
                    </span>
                  )}
                  <ChevronRight size={14} className="text-ink-4" />
                </div>
              </button>
            ) : (
              <div className="flex items-center gap-2 bg-amber/10 border border-amber/20 rounded-lg px-3 py-2.5">
                <AlertCircle size={14} className="text-amber flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-[10px] text-amber font-semibold">No MT5 accounts linked.</p>
                  <Link to="/accounts/new" className="text-[10px] text-amber underline hover:text-amber/80">
                    Add MT5 Account
                  </Link>
                </div>
              </div>
            )}
            {errorField === 'account' && (
              <p className="text-[10px] text-orange mt-1 px-1 animate-[forge-fade-in_0.2s_ease-out]">Please select an MT5 account.</p>
            )}
          </div>

          {/* Strategy — selector button (hidden for copy trading) */}
          {!isCopyTrade && <div>
            <label className="block text-[11px] font-semibold text-ink-3 mb-1.5">
              Strategy to run *
            </label>
            {allPublished.length === 0 ? (
              <div className="flex items-center gap-2 bg-amber/10 border border-amber/20 rounded-lg px-3 py-2.5">
                <AlertCircle size={14} className="text-amber flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-[10px] text-amber font-semibold">No published strategies yet.</p>
                  <Link to="/strategies/new" className="text-[10px] text-amber underline hover:text-amber/80">
                    Create and publish a strategy first
                  </Link>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => { setShowStrategyPicker(true); setErrorField(null); }}
                className={`w-full text-left rounded-lg border px-3 py-2 transition-all cursor-pointer flex items-center justify-between bg-surface ${
                  errorField === 'strategy'
                    ? 'border-orange'
                    : 'border-glass-border hover:border-glass-border/80'
                }`}
              >
                {selectedStrategy ? (
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Bot size={14} className="text-orange flex-shrink-0" />
                    <span className="text-sm text-ink truncate">{selectedStrategy.name}</span>
                  </div>
                ) : (
                  <span className="text-sm text-ink-4">Select a strategy...</span>
                )}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {selectedStrategy?.symbol && (
                    <span className="text-[9px] font-mono font-semibold text-ink-2 bg-surface-2 rounded px-1.5 py-0.5">
                      {selectedStrategy.symbol}
                    </span>
                  )}
                  {selectedStrategy && selectedStrategy.latest_version > 0 && (
                    <span className="text-[9px] font-mono font-bold text-orange bg-orange/10 rounded px-1 py-0.5">
                      v{selectedStrategy.latest_version}
                    </span>
                  )}
                  <ChevronRight size={14} className="text-ink-4" />
                </div>
              </button>
            )}
            {/* Unsupported symbol warning when a strategy is selected */}
            {selectedStrategy?.symbol && !isSymbolSupported(selectedStrategy.symbol, brokerSymbols, brokerSymbolsUpper) && (
              <div className="flex items-center gap-1.5 mt-1.5 px-1">
                <AlertTriangle size={10} className="text-amber flex-shrink-0" />
                <span className="text-[10px] text-amber">
                  {selectedStrategy.symbol} is not available on the selected MT5 account
                </span>
              </div>
            )}
            {errorField === 'strategy' && (
              <p className="text-[10px] text-orange mt-1 px-1 animate-[forge-fade-in_0.2s_ease-out]">Please select a strategy.</p>
            )}
          </div>}

          {/* Assigned Capital */}
          <div>
            <Input
              label="Assigned Capital (USD) *"
              type="number"
              placeholder="10000"
              min="1"
              step="1"
              value={capital}
              onChange={(e) => { setCapital(e.target.value); if (errorField === 'capital') setErrorField(null); }}
              className={exceedsCapital || errorField === 'capital' ? '!border-orange' : ''}
              required
            />
            {isLive && selectedAccount && selectedAccount.unallocated_capital != null && (
              <div className="flex items-center gap-1 mt-1 px-1">
                <p className={`text-[10px] ${selectedAccount.unallocated_capital <= 0 ? 'text-red' : capitalNum > selectedAccount.unallocated_capital ? 'text-red' : 'text-ink-4'}`}>
                  Available: ${selectedAccount.unallocated_capital.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  {selectedAccount.last_sync_time && (
                    <span className="text-ink-4"> (as of {new Date(Number(selectedAccount.last_sync_time)).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})</span>
                  )}
                </p>
                <button
                  type="button"
                  onClick={() => selectedAccount && syncBalance.mutate(selectedAccount.id)}
                  disabled={syncBalance.isPending}
                  className="text-ink-4 hover:text-orange transition-colors cursor-pointer disabled:opacity-50"
                  title="Sync balance from broker"
                >
                  <RefreshCw size={10} className={syncBalance.isPending ? 'animate-spin' : ''} />
                </button>
              </div>
            )}
            {isLive && selectedAccount && capitalNum > 0 && capitalNum > selectedAccount.unallocated_capital && (
              <p className="text-[10px] text-red mt-0.5 px-1">
                Exceeds available capital by ${(capitalNum - selectedAccount.unallocated_capital).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            )}
          </div>

          {/* Leverage — always from MT5 account */}
          {selectedAccount && (
            <div className="flex items-center gap-1.5 px-1">
              <span className="text-[10px] text-ink-4">
                Account leverage: <span className="font-bold text-ink-2">1:{selectedAccount.leverage}</span>
              </span>
            </div>
          )}
        </div>

        {/* ─── Section 5: Allocation Mode ─── */}
        <div className="glass-sm rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-1.5">
              <Copy size={12} className="text-violet" />
              <p className="text-[11px] font-bold text-ink-3 uppercase tracking-wider">
                Allocation Mode
              </p>
            </div>
            {!isCopyTrade && (
              <button
                type="button"
                onClick={() => setAllocModeEnabled(!allocModeEnabled)}
                className={`w-8 h-4 rounded-full transition-colors cursor-pointer relative ${
                  allocModeEnabled ? 'bg-violet' : 'bg-surface-2'
                }`}
              >
                <span
                  className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${
                    allocModeEnabled ? 'left-4' : 'left-0.5'
                  }`}
                />
              </button>
            )}
          </div>

          {!allocModeEnabled ? (
            <p className="text-[10px] text-ink-4">
              Direct — strategy's own lot sizing, no override.
            </p>
          ) : (
            <div className="space-y-3">
              {/* Mode cards */}
              <div className="grid grid-cols-2 gap-2">
                {(Object.entries(ALLOC_MODE_INFO) as [AllocMode, { label: string; description: string }][]).map(([m, info]) => {
                  const selected = allocMode === m;
                  return (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setAllocMode(m)}
                      className={`relative text-left rounded-lg border px-3 py-2.5 transition-all duration-200 cursor-pointer ${
                        selected
                          ? 'border-violet/40 bg-violet/5'
                          : 'border-glass-border bg-surface/50 hover:bg-surface hover:border-glass-border/80'
                      }`}
                    >
                      <div className={`absolute top-2 right-2 w-2 h-2 rounded-full transition-all ${
                        selected ? 'bg-violet' : 'bg-ink-4/30'
                      }`} />
                      <span className={`text-[11px] font-bold ${selected ? 'text-violet' : 'text-ink-3'}`}>
                        {info.label}
                      </span>
                      <p className="text-[9px] text-ink-4 mt-0.5 leading-tight">{info.description}</p>
                    </button>
                  );
                })}
              </div>

              {/* Mode-specific input */}
              {allocMode === 'FIXED_LOT' && (
                <div>
                  <div className="flex items-center gap-2">
                    <Input
                      label="Lot Size *"
                      type="number"
                      placeholder="0.01"
                      min="0.001"
                      step="any"
                      value={allocFixedLot}
                      onChange={(e) => setAllocFixedLot(e.target.value)}
                      required
                    />
                  </div>
                  {(() => {
                    const spec = selectedAccount?.symbol_specs?.[symbol];
                    if (!spec) return null;
                    return (
                      <div className="flex items-center gap-2 mt-1.5 px-0.5">
                        <span className="text-[9px] text-ink-4">Broker range:</span>
                        <span className="text-[9px] font-mono font-semibold text-violet bg-violet/10 rounded px-1.5 py-0.5">
                          {spec.minVolume} — {spec.maxVolume}
                        </span>
                      </div>
                    );
                  })()}
                </div>
              )}

              {allocMode === 'FIXED_MULT' && (
                <div>
                  <Input
                    label="Multiplier *"
                    type="number"
                    placeholder="1.0"
                    min="0.001"
                    step="any"
                    value={allocMultiplier}
                    onChange={(e) => setAllocMultiplier(e.target.value)}
                    required
                  />
                  {allocMultiplier && parseFloat(allocMultiplier) > 0 && (
                    <p className="text-[9px] text-ink-4 mt-1.5 px-0.5">
                      Each position opened will be <span className="font-semibold text-ink-3">{allocMultiplier}x</span> the size computed by the strategy.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ─── Section 6: Risk Management ─── */}
        <div className="glass-sm rounded-lg p-4">
          <div className="flex items-center gap-1.5 mb-4">
            <Shield size={12} className="text-red" />
            <p className="text-[11px] font-bold text-ink-3 uppercase tracking-wider">
              Risk Management
            </p>
          </div>

          {/* Stop Loss */}
          <div className="bg-surface/50 rounded-lg p-3 border border-red/10">
            <div className="flex items-center justify-between mb-1">
              <label className="text-[11px] font-semibold text-ink-3 flex items-center gap-1.5">
                <Shield size={10} className="text-red" />
                Equity Stop Loss
              </label>
              <span className="text-sm font-extrabold font-mono text-red min-w-[3ch] text-right">
                {slPct}%
              </span>
            </div>
            <p className="text-[9px] text-ink-4 mb-2.5">
              Bot stops when equity drops this percentage below assigned capital.
            </p>
            <input
              type="range"
              min="5"
              max="95"
              value={slPct}
              onChange={(e) => setSlPct(e.target.value)}
              className="w-full accent-red h-1 rounded-full cursor-pointer"
            />
            <div className="flex justify-between mt-1">
              <span className="text-[8px] text-ink-4 font-mono">5%</span>
              <span className="text-[8px] text-ink-4 font-mono">95%</span>
            </div>
            {capitalNum > 0 && (
              <div className="mt-2 pt-2 border-t border-red/5">
                <div className="flex items-center justify-between">
                  <span className="text-[9px] text-ink-4">Estimated max loss</span>
                  <span className="text-[11px] font-bold font-mono text-red">
                    -${slDollar.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Take Profit */}
          <div className={`mt-3 rounded-lg p-3 border transition-colors ${tpEnabled ? 'bg-surface/50 border-green/10' : 'bg-surface/30 border-glass-border/30'}`}>
            <div className="flex items-center justify-between">
              <label className="text-[11px] font-semibold text-ink-3 flex items-center gap-1.5">
                <Target size={10} className={tpEnabled ? 'text-green' : 'text-ink-4'} />
                Equity Take Profit
              </label>
              <div className="flex items-center gap-2">
                {tpEnabled && (
                  <span className="text-sm font-extrabold font-mono text-green min-w-[3ch] text-right">
                    {tpPct}%
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => setTpEnabled(!tpEnabled)}
                  className={`w-8 h-4 rounded-full transition-colors cursor-pointer relative ${
                    tpEnabled ? 'bg-green' : 'bg-surface-2'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${
                      tpEnabled ? 'left-4' : 'left-0.5'
                    }`}
                  />
                </button>
              </div>
            </div>
            {!tpEnabled && (
              <p className="text-[9px] text-ink-4 mt-1">
                No profit target — bot runs until manually stopped.
              </p>
            )}
            {tpEnabled && (
              <div className="mt-1">
                <p className="text-[9px] text-ink-4 mb-2.5">
                  Bot stops when equity gains this percentage of assigned capital.
                </p>
                <input
                  type="range"
                  min="5"
                  max="95"
                  value={tpPct}
                  onChange={(e) => setTpPct(e.target.value)}
                  className="w-full accent-green h-1 rounded-full cursor-pointer"
                />
                <div className="flex justify-between mt-1">
                  <span className="text-[8px] text-ink-4 font-mono">5%</span>
                  <span className="text-[8px] text-ink-4 font-mono">95%</span>
                </div>
                {capitalNum > 0 && (
                  <div className="mt-2 pt-2 border-t border-green/5">
                    <div className="flex items-center justify-between">
                      <span className="text-[9px] text-ink-4">Estimated gain</span>
                      <span className="text-[11px] font-bold font-mono text-green">
                        +${tpDollar.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ─── Error ─── */}
        {error && (
          <div className="flex items-center gap-2 bg-red/10 border border-red/20 rounded-lg px-3 py-2">
            <AlertCircle size={14} className="text-red flex-shrink-0" />
            <p className="text-[10px] text-red font-semibold">{error}</p>
          </div>
        )}

        {/* ─── Actions ─── */}
        <Button
          type="submit"
          disabled={!hasAccounts || atBotLimit}
          className={`w-full ${
            atBotLimit
              ? ''
              : isLive
                ? 'bg-green/90 hover:bg-green text-white border-transparent shadow-none hover:shadow-[0_0_12px_rgba(34,197,94,0.2)]'
                : 'bg-blue/90 hover:bg-blue text-white border-transparent shadow-none hover:shadow-[0_0_12px_rgba(59,130,246,0.2)]'
          }`}
        >
          {atBotLimit ? (
            <>
              <Lock size={14} />
              Bot Limit Reached
            </>
          ) : (
            <>
              {isLive ? <Radio size={14} /> : <FlaskConical size={14} />}
              {isLive ? 'Create Live Bot' : 'Create Paper Bot'}
            </>
          )}
        </Button>
      </form>

      {/* ─── Preview Modal ─── */}
      <Modal
        open={showPreview}
        onClose={() => setShowPreview(false)}
        title="Review Bot Configuration"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setShowPreview(false)}
              disabled={createBot.isPending}
            >
              Back
            </Button>
            <Button
              onClick={handleConfirmCreate}
              disabled={createBot.isPending}
              className={
                isLive
                  ? 'bg-green/90 hover:bg-green text-white border-transparent'
                  : 'bg-blue/90 hover:bg-blue text-white border-transparent'
              }
            >
              {createBot.isPending ? (
                <>
                  <Spinner size="sm" />
                  Creating...
                </>
              ) : (
                <>
                  {isLive ? <Radio size={14} /> : <FlaskConical size={14} />}
                  {isLive ? 'Create Live Bot' : 'Create Paper Bot'}
                </>
              )}
            </Button>
          </>
        }
      >
        <div className="glass-sm rounded-lg p-4 space-y-3">
          {/* Mode */}
          <PreviewRow label="Mode">
            <span
              className={`inline-flex items-center gap-1.5 text-xs font-bold px-2 py-0.5 rounded-full ${
                isLive
                  ? 'bg-green/10 text-green'
                  : 'bg-blue/10 text-blue'
              }`}
            >
              {isLive ? <Radio size={10} /> : <FlaskConical size={10} />}
              {isLive ? 'LIVE' : 'PAPER'}
            </span>
          </PreviewRow>

          {/* Name */}
          <PreviewRow label="Name">
            <span className="text-sm font-semibold text-ink">{name.trim()}</span>
          </PreviewRow>

          {/* MT5 Account */}
          {selectedAccount && (
            <PreviewRow label="MT5 Account">
              <span className="text-sm font-mono text-ink-2">
                {selectedAccount.name || selectedAccount.login_id}@{selectedAccount.server}
              </span>
            </PreviewRow>
          )}

          {/* Strategy */}
          <PreviewRow label="Strategy">
            {selectedStrategy ? (
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-semibold text-ink">{selectedStrategy.name}</span>
                {selectedStrategy.latest_version > 0 && (
                  <span className="text-[9px] font-mono font-bold text-ink-4 bg-surface-2 rounded px-1 py-0.5">
                    v{selectedStrategy.latest_version}
                  </span>
                )}
                {selectedStrategy.symbol && (
                  <span className="text-[9px] font-mono font-semibold text-orange bg-orange/10 rounded px-1.5 py-0.5">
                    {selectedStrategy.symbol}
                  </span>
                )}
              </div>
            ) : (
              <span className="text-sm text-ink-4">None</span>
            )}
          </PreviewRow>

          {/* Symbol */}
          <PreviewRow label="Symbol">
            <span className="text-sm font-mono font-semibold text-orange">{symbol}</span>
          </PreviewRow>

          {/* Capital */}
          <PreviewRow label="Capital">
            <span className="text-sm font-bold text-green">
              ${parseFloat(capital).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </PreviewRow>

          {/* Leverage */}
          <PreviewRow label="Leverage">
            <span className="text-sm font-mono text-ink-2">
              1:{selectedAccount?.leverage ?? 100}
            </span>
          </PreviewRow>

          {/* Allocation Mode */}
          <PreviewRow label="Allocation">
            {allocModeEnabled ? (
              <div className="text-right">
                <span className="text-xs font-bold text-violet">
                  {ALLOC_MODE_INFO[allocMode].label}
                </span>
                <span className="block text-[10px] text-ink-4 font-mono">
                  {allocMode === 'FIXED_LOT' && `${allocFixedLot} lots`}
                  {allocMode === 'FIXED_MULT' && `${allocMultiplier}x`}
                </span>
              </div>
            ) : (
              <span className="text-xs text-ink-4">Direct</span>
            )}
          </PreviewRow>

          {/* Stop Loss */}
          <PreviewRow label="Stop Loss">
            <span className="text-sm font-bold text-red">
              {slPct}%
              {slDollar > 0 && (
                <span className="font-normal text-ink-3 ml-1">
                  (-${slDollar.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                </span>
              )}
            </span>
          </PreviewRow>

          {/* Take Profit */}
          <PreviewRow label="Take Profit">
            {tpEnabled ? (
              <span className="text-sm font-bold text-green">
                {tpPct}%
                {tpDollar > 0 && (
                  <span className="font-normal text-ink-3 ml-1">
                    (+${tpDollar.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                  </span>
                )}
              </span>
            ) : (
              <span className="text-sm text-ink-4">Disabled</span>
            )}
          </PreviewRow>
        </div>

        {/* Stale balance info */}
        {isLive && selectedAccount?.last_sync_time && (
          <div className="flex items-start gap-2 mt-3 px-1">
            <Info size={12} className="text-ink-4 flex-shrink-0 mt-0.5" />
            <p className="text-[10px] text-ink-4 leading-relaxed">
              Balance was last synced on {new Date(Number(selectedAccount.last_sync_time)).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}.
              Actual balance will be verified when the bot starts.
            </p>
          </div>
        )}
      </Modal>

      {/* ─── MT5 Account Picker Modal ─── */}
      <Modal
        open={showAccountPicker}
        onClose={() => setShowAccountPicker(false)}
        title="Select MT5 Account"
      >
        <div className="space-y-2">
          {(accounts ?? []).map((acc) => {
            const isSelected = mt5AccountId === acc.id;
            const symbolCount = acc.symbol_specs ? Object.keys(acc.symbol_specs).length : 0;
            return (
              <button
                key={acc.id}
                type="button"
                onClick={() => {
                  setMt5AccountId(acc.id);
                  setTemplateId('');
                  setShowAccountPicker(false);
                }}
                className={`w-full text-left rounded-lg border px-4 py-3 transition-all duration-200 cursor-pointer ${
                  isSelected
                    ? 'border-orange/40 bg-orange/5 shadow-[0_0_12px_rgba(249,115,22,0.08)]'
                    : 'border-glass-border bg-surface/50 hover:bg-surface hover:border-glass-border/80'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className={`w-3 h-3 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      isSelected ? 'border-orange' : 'border-ink-4/30'
                    }`}>
                      {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-orange" />}
                    </div>
                    <div>
                      <span className={`text-xs font-bold block ${isSelected ? 'text-ink' : 'text-ink-2'}`}>
                        {acc.name || acc.login_id}
                      </span>
                      <span className="text-[10px] text-ink-4 font-mono">{acc.server}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    {acc.synced_balance != null && (
                      <>
                        <span className="text-xs font-bold text-ink block">
                          ${acc.synced_balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                        {acc.last_sync_time && (
                          <span className="text-[9px] text-ink-4 block">
                            as of {new Date(Number(acc.last_sync_time)).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>
                {/* Details row */}
                <div className="mt-2 pt-2 border-t border-glass-border/30 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span className="text-[9px] text-ink-4">
                      Leverage <span className="font-bold text-ink-3">1:{acc.leverage}</span>
                    </span>
                    <span className="text-[9px] text-ink-4">
                      {acc.currency}
                    </span>
                    {symbolCount > 0 && (
                      <span className="text-[9px] text-ink-4">
                        {symbolCount} symbols
                      </span>
                    )}
                  </div>
                  {acc.unallocated_capital != null && (
                    <span className={`text-[10px] font-bold ${acc.unallocated_capital >= 0 ? 'text-green' : 'text-red'}`}>
                      ${acc.unallocated_capital.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} available
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </Modal>

      {/* ─── Strategy Picker Modal ─── */}
      <Modal
        open={showStrategyPicker}
        onClose={() => setShowStrategyPicker(false)}
        title="Select Strategy"
      >
        <div className="space-y-2">
          {allPublished.map((s) => {
            const isSelected = templateId === s.id;
            const supported = isSymbolSupported(s.symbol, brokerSymbols, brokerSymbolsUpper);
            const rules = s.rules as { entries?: unknown[]; exits?: unknown[] } | null;
            const entryCount = Array.isArray(rules?.entries) ? rules.entries.length : 0;
            const exitCount = Array.isArray(rules?.exits) ? rules.exits.length : 0;
            return (
              <button
                key={s.id}
                type="button"
                disabled={!supported}
                onClick={() => {
                  if (!supported) return;
                  setTemplateId(s.id);
                  if (s.symbol) setSymbol(s.symbol);
                  setShowStrategyPicker(false);
                }}
                className={`w-full text-left rounded-lg border px-4 py-3 transition-all duration-200 ${
                  !supported
                    ? 'border-glass-border glass-sm opacity-40 cursor-not-allowed'
                    : isSelected
                      ? 'border-orange/40 bg-orange/5 cursor-pointer'
                      : 'border-glass-border glass-sm hover:border-orange/15 cursor-pointer'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className={`w-3 h-3 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      isSelected ? 'border-orange' : 'border-ink-4/30'
                    }`}>
                      {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-orange" />}
                    </div>
                    <div className="min-w-0">
                      <span className={`text-xs font-bold block truncate ${isSelected ? 'text-ink' : 'text-ink-2'}`}>
                        {s.name}
                      </span>
                      {s.description && (
                        <span className="text-[10px] text-ink-4 truncate block mt-0.5">{s.description}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                    {s.symbol && (
                      <span className={`text-[9px] font-mono font-semibold rounded px-1.5 py-0.5 ${
                        supported ? 'text-ink-2 bg-surface-2' : 'text-amber bg-amber/10'
                      }`}>
                        {s.symbol}
                      </span>
                    )}
                    {!supported && <AlertTriangle size={10} className="text-amber" />}
                    {s.latest_version > 0 && (
                      <span className="text-[9px] font-mono font-bold text-orange bg-orange/10 rounded px-1 py-0.5">
                        v{s.latest_version}
                      </span>
                    )}
                  </div>
                </div>
                {/* Details row */}
                <div className="mt-2 pt-2 border-t border-glass-border/30 flex items-center gap-3">
                  <span className="text-[9px] text-ink-4">
                    {s.strategy_type === 'CONDITION_ENGINE' ? 'Condition Engine' : s.strategy_type}
                  </span>
                  {entryCount > 0 && (
                    <span className="text-[9px] text-ink-4">
                      {entryCount} entr{entryCount === 1 ? 'y' : 'ies'}
                    </span>
                  )}
                  {exitCount > 0 && (
                    <span className="text-[9px] text-ink-4">
                      {exitCount} exit{exitCount === 1 ? '' : 's'}
                    </span>
                  )}
                  {s.latest_backtest && (s.latest_backtest as any).netPnl != null && (s.latest_backtest as any).netPnl !== 0 && (
                    <span className={`text-[9px] font-bold ${(s.latest_backtest as any).netPnl >= 0 ? 'text-green' : 'text-red'}`}>
                      {(s.latest_backtest as any).netPnl >= 0 ? '+' : ''}${((s.latest_backtest as any).netPnl).toFixed(2)}
                    </span>
                  )}
                  {!supported && (
                    <span className="text-[9px] text-amber font-semibold ml-auto">
                      Unsupported symbol
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </Modal>
    </div>
  );
}

// ─── Preview Row ─────────────────────────────────────

function PreviewRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-glass-border/30 last:border-0">
      <span className="text-[11px] font-semibold text-ink-3">{label}</span>
      <div>{children}</div>
    </div>
  );
}

// ─── Mode Toggle Button ──────────────────────────────

function ModeButton({
  active,
  onClick,
  icon,
  label,
  description,
  accentClass,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  description: string;
  accentClass: 'green' | 'blue';
}) {
  const colors = {
    green: {
      activeBg: 'bg-green/10',
      activeBorder: 'border-green/40',
      activeText: 'text-green',
      activeGlow: 'shadow-[0_0_12px_rgba(34,197,94,0.1)]',
    },
    blue: {
      activeBg: 'bg-blue/10',
      activeBorder: 'border-blue/40',
      activeText: 'text-blue',
      activeGlow: 'shadow-[0_0_12px_rgba(59,130,246,0.1)]',
    },
  };

  const c = colors[accentClass];

  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative flex flex-col items-center gap-1.5 rounded-lg border px-3 py-3 transition-all duration-200 cursor-pointer ${
        active
          ? `${c.activeBg} ${c.activeBorder} ${c.activeGlow}`
          : 'bg-surface/50 border-glass-border hover:bg-surface hover:border-glass-border/80'
      }`}
    >
      {/* Radio dot */}
      <div
        className={`absolute top-2 right-2 w-2 h-2 rounded-full transition-all ${
          active ? `${c.activeText} bg-current` : 'bg-ink-4/30'
        }`}
      />

      <div className={`${active ? c.activeText : 'text-ink-4'} transition-colors`}>
        {icon}
      </div>
      <span
        className={`text-xs font-bold ${active ? 'text-ink' : 'text-ink-3'} transition-colors`}
      >
        {label}
      </span>
      <span className="text-[9px] text-ink-4 leading-tight text-center">
        {description}
      </span>
    </button>
  );
}

